import loadjs from 'loadjs';
import * as Sentry from '@sentry/nextjs';
import { CustomError, ScriptError } from '@/types/errors';

export interface ScriptOptions {
	src: string;
	success?: () => void;
	error?: () => void;
	bundleId?: string;
	mustBeFirst?: boolean;
	type?: 'module' | 'text/javascript';
}

// Keep track of loaded scripts and their names
const loadedScripts: Record<string, string> = {};

// Helper to extract filename from URL or stack trace
const getScriptName = (url: string) => {
	try {
		return new URL(url).pathname.split('/').pop() || url;
	} catch {
		return url;
	}
};

// Helper to identify third-party scripts
const isThirdPartyScript = (filename: string) => {
	try {
		// Get current domain
		const currentDomain = window.location.hostname;
		// Parse the script URL
		const scriptDomain = new URL(filename).hostname;
		// Return true if script is from a different domain
		return scriptDomain !== currentDomain;
	} catch {
		// If URL parsing fails, return false to be safe
		return false;
	}
};

// Set up global error handler
if (typeof window !== 'undefined') {
	window.addEventListener('error', (event) => {
		// Try to determine if error came from one of our loaded scripts
		const errorScript = event.filename && getScriptName(event.filename);
		const matchedScript = errorScript && loadedScripts[errorScript];

		if (matchedScript || (errorScript && isThirdPartyScript(event.filename))) {
			Sentry.withScope((scope) => {
				scope.setTag('script', matchedScript || 'third-party');
				scope.setTag('errorType', 'runtime');
				scope.setTag('scriptFile', errorScript);
				scope.setLevel('error');
				Sentry.captureException(event.error);
			});
		}
	});
}

const detectLoadFailureType = (
	src: string,
): { type: 'blocked' | 'network' | 'unknown'; finalUrl?: string } => {
	if (!src || typeof src !== 'string') {
		throw new ScriptError('Invalid source URL provided');
	}

	// iOS Safari might modify the URL (e.g., add cache busting parameters)
	// so we use a partial match for the querySelector
	const scriptElement = document.querySelector(`script[src*="${src.split('?')[0]}"]`);

	if (!scriptElement) {
		// Script element is missing from DOM, likely due to:
		// - Content Security Policy (CSP) blocking script creation
		// - Browser security policy
		// - DOM manipulation prevention
		// - iOS memory constraints
		return { type: 'blocked' };
	}

	// Get the final URL from the script element
	let finalUrl: string | undefined;
	if (scriptElement instanceof HTMLScriptElement) {
		finalUrl = scriptElement.src;
	}

	// Script element exists but failed to load. This could be due to:
	// - Ad-blocker intercepting the request
	// - Network failure
	// - 404 or other HTTP error
	// - CORS issue
	// - iOS Safari specific issues (stricter CSP, memory limits)
	// Without access to detailed network info, we can't distinguish between these
	return { type: 'network', finalUrl };
};

export const loadScript = (options: ScriptOptions, name: string) => {
	const bundleId = options.bundleId || options.src.replace(/[^\w]/g, '');
	const scriptName = getScriptName(options.src);

	if (loadjs.isDefined(bundleId)) {
		return;
	}

	// Register this script
	loadedScripts[scriptName] = name;

	// Handle module scripts differently
	if (options.type === 'module' || options.src.endsWith('.mjs')) {
		// For module scripts, we need to create the script element manually
		// since loadjs doesn't handle module scripts well
		const script = document.createElement('script');
		script.src = options.src;
		script.type = 'module';
		script.setAttribute('SameSite', 'None; Secure');

		script.onload = () => {
			try {
				options.success?.();
			} catch (err) {
				Sentry.withScope((scope) => {
					scope.setTag('script', name);
					scope.setTag('errorType', 'runtime-callback');
					scope.setLevel('error');
					Sentry.captureException(new ScriptError(err as Error));
				});
			}
		};

		script.onerror = () => {
			Sentry.withScope((scope) => {
				scope.setTag('script', name);
				scope.setTag('errorType', 'loading');
				scope.setTag('bundleId', bundleId);
				scope.setTag('failureType', 'network');
				scope.setLevel('error');
				Sentry.setContext('scriptDetails', {
					originalSrc: options.src,
					type: 'module',
					hasSuccessCallback: !!options.success,
				});
				Sentry.captureException(
					new CustomError(`Failed to load module script: ${name}`, `${name} Module Load Error`),
				);
			});
			options.error?.();
		};

		document.head.appendChild(script);
		return;
	}

	loadjs(options.src, bundleId, {
		before: (_path: string, scriptEl: HTMLScriptElement) => {
			// Handle position
			if (options.mustBeFirst) {
				const container =
					document.getElementsByTagName('head')[0] || document.getElementsByTagName('body')[0];
				const firstScript = container.getElementsByTagName('script')[0];
				if (firstScript) {
					firstScript.parentNode?.insertBefore(scriptEl, firstScript);
					return false; // Prevent loadjs from inserting the script
				}
			}
			return true; // Let loadjs handle normal insertion
		},
		success: () => {
			try {
				options.success?.();
			} catch (err) {
				Sentry.withScope((scope) => {
					scope.setTag('script', name);
					scope.setTag('errorType', 'runtime-callback');
					scope.setLevel('error');
					Sentry.captureException(new ScriptError(err as Error));
				});
			}
		},
		error: (pathsNotFound) => {
			Sentry.withScope((scope) => {
				let failureType: 'blocked' | 'network' | 'unknown';
				let scriptInDOM = false;
				let finalUrl: string | undefined;

				try {
					const result = detectLoadFailureType(options.src);
					failureType = result.type;
					finalUrl = result.finalUrl;
					scriptInDOM = failureType === 'network';
				} catch (e) {
					// Report the error to Sentry but don't let it break the script loading
					scope.setTag('errorType', 'detection-error');
					Sentry.setContext('detectionDetails', {
						src: options.src,
						error: e instanceof Error ? e.message : 'Unknown error',
					});
					Sentry.captureException(new ScriptError(e as Error));
					failureType = 'unknown';
				}

				const errorName = `${name.startsWith('GTM') ? 'Google Tag Manager' : name} Load Error`;

				scope.setTag('script', name);
				scope.setTag('errorType', 'loading');
				scope.setTag('bundleId', bundleId);
				scope.setTag('failureType', failureType);
				scope.setLevel('error');
				Sentry.setContext('scriptDetails', {
					originalSrc: options.src,
					finalUrl,
					failedPaths: pathsNotFound,
					mustBeFirst: !!options.mustBeFirst,
					hasSuccessCallback: !!options.success,
					failureType,
					scriptInDOM,
				});
				Sentry.captureException(
					new CustomError(
						`Failed to load script: ${name} (${failureType}), pathsNotFound: ${pathsNotFound[0]}`,
						errorName,
					),
				);
			});
			options.error?.();
		},
	});
};
